/**
 * ===================================================================================================
 * RoomCreationModal.js - 새로운 조율방을 생성하기 위한 복합 모달 컴포넌트
 * ===================================================================================================
 *
 * 📍 위치: 프론트엔드 > client/src/components/modals/RoomCreationModal.js
 *
 * 🎯 주요 기능:
 *    - 조율방의 기본 정보(이름, 설명, 최대 멤버 수)를 입력받는 폼 제공.
 *    - 조율방의 시간표 운영 시간(시작/종료 시간) 설정.
 *    - 조율방 전체에 적용될 '금지 시간대'를 수동으로 추가/삭제하는 기능.
 *    - '방장 개인시간 연동' 기능: 방장의 개인 스케줄(개인시간, 예외시간)을 자동으로 불러와
 *      조율방의 '배정 불가 시간'으로 설정하는 핵심 기능.
 *
 * 🔗 연결된 파일:
 *    - ./CoordinationTab.js (추정) - '새 조율방 생성' 버튼 클릭 시 이 모달을 호출.
 *    - ../../services/userService.js - 방장의 개인 스케줄을 가져오기 위해 API를 호출.
 *
 * 💡 UI 위치:
 *    - '일정 맞추기' 탭에서 '새 조율방 생성' 버튼을 클릭했을 때 나타나는 팝업 모달.
 *
 * ✏️ 수정 가이드:
 *    - '방장 개인시간 연동' 로직을 수정하려면 `syncOwnerSchedule` 상태를 다루는 `useEffect` 훅을 수정해야 합니다.
 *      이곳에는 개인시간, 예외시간을 `roomExceptions` 형태로 변환하는 복잡한 로직이 포함되어 있습니다.
 *    - 방 생성 시 서버로 전달되는 데이터 구조를 변경하려면 `handleSubmit` 함수 내 `roomData` 객체를 수정합니다.
 *
 * 📝 참고사항:
 *    - 이 모달은 `useEffect`를 통해 마운트 시 방장의 최신 스케줄 정보를 비동기적으로 가져옵니다.
 *    - '개인시간 연동' 기능은 방장의 선호시간(`defaultSchedule`)은 제외하고, 개인적인 약속(`scheduleExceptions`)과
 *      고정적인 개인시간(`personalTimes`)만을 '배정 불가 시간'으로 변환하여 연동합니다.
 *    - 자정이 넘어가는 개인시간(예: 수면)은 두 개의 예외 시간으로 분리하여 처리하는 로직이 포함되어 있습니다.
 *
 * ===================================================================================================
 */
import React, { useState, useEffect } from 'react';
import { X, Link, Share2, UserPlus, Sparkles, CheckCircle2, Circle } from 'lucide-react';
import CustomAlertModal from './CustomAlertModal';
import { userService } from '../../services/userService';
import { auth } from '../../config/firebaseConfig';
import { API_BASE_URL } from '../../config/apiConfig';

const API_BASE = API_BASE_URL;

/**
 * RoomCreationModal
 * @description 새로운 조율방을 생성하기 위한 상세 설정 UI를 제공하는 모달 컴포넌트.
 * @param {object} props - 컴포넌트 props
 * @param {function} props.onClose - 모달을 닫는 함수.
 * @param {function} props.onCreateRoom - '생성' 버튼 클릭 시 호출되는 콜백. 최종적인 방 설정 데이터를 인자로 받음.
 * @param {object} props.ownerProfileSchedule - 초기에 전달받는 방장의 스케줄 데이터.
 * @returns {JSX.Element}
 */
const RoomCreationModal = ({ onClose, onCreateRoom, ownerProfileSchedule: initialOwnerSchedule, user }) => {
  const [ownerProfileSchedule, setOwnerProfileSchedule] = useState(initialOwnerSchedule);
  const [name, setName] = useState('');
  const [showAiModes, setShowAiModes] = useState(false);
  const [roomAiModes, setRoomAiModes] = useState([]);
  const [settings, setSettings] = useState({
    startHour: 9,
    endHour: 18,
    blockedTimes: [], // 금지 시간대 배열
    roomExceptions: [], // 새로운 roomExceptions 배열
  });
  
  const [newBlockedTime, setNewBlockedTime] = useState({
    name: '',
    startTime: '12:00',
    endTime: '13:00'
  });

  const [syncOwnerSchedule] = useState(true); // 방장 시간표 자동 연동 (항상 활성화)
  const mode = 'conversational'; // 항상 대화형 모드

  // 2단계: 친구 초대
  const [step, setStep] = useState('create'); // 'create' | 'invite'
  const [createdRoom, setCreatedRoom] = useState(null);
  const [creating, setCreating] = useState(false);

  // 초대 시트 상태
  const [invitePage, setInvitePage] = useState('main'); // 'main' | 'friends'
  const [inviteSelected, setInviteSelected] = useState([]);
  const [inviteFriends, setInviteFriends] = useState([]);
  const [inviteFriendsLoading, setInviteFriendsLoading] = useState(false);

  // CustomAlert 상태
  const [customAlert, setCustomAlert] = useState({ show: false, message: '' });
  const showAlert = (message) => setCustomAlert({ show: true, message });
  const closeAlert = () => setCustomAlert({ show: false, message: '' });

  // 컴포넌트 마운트 시 최신 사용자 일정 데이터 로드
  useEffect(() => {
    const loadOwnerSchedule = async () => {
      try {
        const scheduleData = await userService.getUserSchedule();
        setOwnerProfileSchedule(scheduleData);
      } catch (err) {
      }
    };

    loadOwnerSchedule();
  }, []);

  // 요일 매핑 (0: 일, 1: 월, ..., 6: 토)
  const dayOfWeekMap = {
    0: '일요일', 1: '월요일', 2: '화요일', 3: '수요일', 4: '목요일', 5: '금요일', 6: '토요일'
  };

useEffect(() => {
    if (syncOwnerSchedule && ownerProfileSchedule) {
      const syncedExceptions = [];

      // ❌ defaultSchedule (선호시간)은 금지시간으로 추가하지 않음
      // 선호시간은 자동배정 시 조원들이 사용할 수 있는 시간이므로 제외

      // scheduleExceptions을 날짜/제목별로 그룹화하여 병합 처리
      const exceptionGroups = {};
      (ownerProfileSchedule.scheduleExceptions || []).forEach(exception => {

        const startDate = new Date(exception.startTime);
        const endDate = new Date(exception.endTime);
        const dateKey = startDate.toLocaleDateString('ko-KR'); // 2025. 9. 30. 형태
        const title = exception.title || '일정';
        const groupKey = `${dateKey}-${title}`;

        if (!exceptionGroups[groupKey]) {
          exceptionGroups[groupKey] = {
            title: title,
            date: dateKey,
            exceptions: []
          };
        }
        exceptionGroups[groupKey].exceptions.push(exception);
      });

      // 각 그룹별로 시간대를 병합하여 roomException 생성
      Object.values(exceptionGroups).forEach(group => {
        // 시간순으로 정렬
        group.exceptions.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

        // 연속된 시간대들을 병합
        const mergedTimeRanges = [];
        let currentRange = null;

        group.exceptions.forEach(exception => {
          const startDate = new Date(exception.startTime);
          const endDate = new Date(exception.endTime);

          if (!currentRange) {
            currentRange = {
              startTime: startDate,
              endTime: endDate,
              originalException: exception
            };
          } else {
            // 현재 범위의 끝과 다음 예외의 시작이 연결되는지 확인
            if (currentRange.endTime.getTime() === startDate.getTime()) {
              // 연속되므로 끝시간을 확장
              currentRange.endTime = endDate;
            } else {
              // 연속되지 않으므로 현재 범위를 저장하고 새로운 범위 시작
              mergedTimeRanges.push(currentRange);
              currentRange = {
                startTime: startDate,
                endTime: endDate,
                originalException: exception
              };
            }
          }
        });

        if (currentRange) {
          mergedTimeRanges.push(currentRange);
        }

        // 병합된 시간대들을 roomException으로 변환
        mergedTimeRanges.forEach(range => {

          // 시간 변환 시 올바른 형식으로 변환 (HH:MM)
          const startTimeStr = `${String(range.startTime.getHours()).padStart(2, '0')}:${String(range.startTime.getMinutes()).padStart(2, '0')}`;
          const endTimeStr = `${String(range.endTime.getHours()).padStart(2, '0')}:${String(range.endTime.getMinutes()).padStart(2, '0')}`;

          const roomException = {
            type: 'date_specific',
            name: `${group.title} (${group.date} ${startTimeStr}~${endTimeStr}) (방장)`,
            startTime: startTimeStr,
            endTime: endTimeStr,
            startDate: range.startTime.toISOString(),
            endDate: range.endTime.toISOString(),
            isSynced: true
          };

          syncedExceptions.push(roomException);
        });
      });

      (ownerProfileSchedule.personalTimes || []).forEach((personalTime, index) => {

        // 반복 개인시간인 경우에만 처리
        if (personalTime.isRecurring !== false && personalTime.days && personalTime.days.length > 0) {
          personalTime.days.forEach(dayOfWeek => {
            // 데이터베이스 요일 시스템 (1=월요일, 2=화요일, ..., 7=일요일)을
            // JavaScript 요일 시스템 (0=일요일, 1=월요일, 2=화요일, ...)으로 변환
            const jsDay = dayOfWeek === 7 ? 0 : dayOfWeek;

            // 시간을 분으로 변환하여 자정 넘나드는지 확인
            const [startHour, startMin] = personalTime.startTime.split(':').map(Number);
            const [endHour, endMin] = personalTime.endTime.split(':').map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            if (endMinutes <= startMinutes) {
              // 밤 부분 (예: 23:00~23:50)
              const nightException = {
                type: 'daily_recurring',
                name: `${personalTime.title || '개인시간'} (방장)`,
                dayOfWeek: jsDay,
                startTime: personalTime.startTime,
                endTime: '23:50',
                isPersonalTime: true,
                isSynced: true
              };

              // 아침 부분 (예: 00:00~07:00)
              const morningException = {
                type: 'daily_recurring',
                name: `${personalTime.title || '개인시간'} (방장)`,
                dayOfWeek: jsDay,
                startTime: '00:00',
                endTime: personalTime.endTime,
                isPersonalTime: true,
                isSynced: true
              };
              syncedExceptions.push(nightException);
              syncedExceptions.push(morningException);
            } else {
              // 일반적인 하루 내 시간
              const personalException = {
                type: 'daily_recurring',
                name: `${personalTime.title || '개인시간'} (방장)`,
                dayOfWeek: jsDay,
                startTime: personalTime.startTime,
                endTime: personalTime.endTime,
                isPersonalTime: true,
                isSynced: true
              };
              syncedExceptions.push(personalException);
            }
          });
        } else {
        }
      });
      // 14:40 관련 예외 확인
      const suspicious = syncedExceptions.filter(ex =>
        ex.startTime?.includes('14:4') ||
        ex.endTime?.includes('15:0') ||
        ex.name?.includes('14:4')
      );
      setSettings(prevSettings => {
        const existingNonSynced = prevSettings.roomExceptions.filter(ex => !ex.isSynced);

        // 14:40 관련 기존 예외 확인
        const suspiciousExisting = existingNonSynced.filter(ex =>
          ex.startTime?.includes('14:4') ||
          ex.endTime?.includes('15:0') ||
          ex.name?.includes('14:4')
        );

        const finalExceptions = [...existingNonSynced, ...syncedExceptions];

        return {
          ...prevSettings,
          roomExceptions: finalExceptions
        };
      });
    }
  }, [syncOwnerSchedule, ownerProfileSchedule]); // ownerProfileSchedule이 변경될 때도 재실행

  const handleSubmit = async () => {
    const finalName = name.trim() || user?.firstName || '';
    if (!finalName) {
      showAlert('방 이름을 입력해주세요.');
      return;
    }
    const roomData = {
      name: finalName,
      description: '',
      mode,
      hasCustomName: name.trim().length > 0,
      aiModes: roomAiModes,
      settings: {
        ...settings,
        roomExceptions: settings.roomExceptions.length > 0 ? settings.roomExceptions : undefined
      }
    };

    setCreating(true);
    try {
      const room = await onCreateRoom(roomData);
      if (room && room.inviteCode) {
        setCreatedRoom(room);
        setInvitePage('friends');
        setInviteFriendsLoading(true);
        setStep('invite');
        try {
          const token = await auth.currentUser?.getIdToken();
          const res = await fetch(`${API_BASE}/api/friends`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            setInviteFriends(data.friends || []);
          }
        } catch {}
        setInviteFriendsLoading(false);
      } else {
        onClose();
      }
    } catch {
      showAlert('방 생성 중 오류가 발생했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyInviteLink = () => {
    const url = `${window.location.origin}/invite/${createdRoom?.inviteCode}`;
    const fallbackCopy = () => {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  const handleShareInviteLink = () => {
    const url = `${window.location.origin}/invite/${createdRoom?.inviteCode}`;
    navigator.share?.({ title: createdRoom?.name, text: `"${createdRoom?.name}"에 초대합니다!`, url }).catch(() => {});
  };

  const toggleInvite = (id) =>
    setInviteSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleOpenFriendsPage = async () => {
    setInvitePage('friends');
    setInviteSelected([]);
    setInviteFriendsLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE}/api/friends`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setInviteFriends(data.friends || []);
      }
    } catch {}
    setInviteFriendsLoading(false);
  };

  const handleInviteFromSheet = async () => {
    if (createdRoom && inviteSelected.length > 0) {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${API_BASE}/api/friends/invite-to-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ inviteCode: createdRoom.inviteCode, friendIds: inviteSelected, roomName: createdRoom.name }),
      }).catch(() => {});
    }
    onClose();
  };

  // 2단계: 초대 bottom sheet
  if (step === 'invite') {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-[55]" onClick={onClose} />
        <div
          className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-[56]"
          style={{ overflow: 'hidden' }}
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          <div style={{ position: 'relative', overflow: 'hidden' }}>
            {/* 친구 선택 */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
                <h3 className="text-base font-bold text-gray-800">친구 초대</h3>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button
                    onClick={handleCopyInviteLink}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: '#dbeafe', border: 'none', borderRadius: 20, cursor: 'pointer' }}
                  >
                    <Link size={14} color="#2563eb" />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#2563eb' }}>링크 복사</span>
                  </button>
                  <button
                    onClick={handleShareInviteLink}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: '#dcfce7', border: 'none', borderRadius: 20, cursor: 'pointer' }}
                  >
                    <Share2 size={14} color="#16a34a" />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a' }}>링크 공유</span>
                  </button>
                  <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div style={{ height: '290px', overflowY: 'auto', padding: '8px' }}>
                {inviteFriendsLoading ? (
                  <div className="flex justify-center py-10">
                    <div style={{ width: 28, height: 28, border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  </div>
                ) : inviteFriends.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <UserPlus size={32} style={{ margin: '0 auto 8px', display: 'block' }} />
                    <p className="text-sm">친구가 없습니다.</p>
                  </div>
                ) : (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {inviteFriends.map(f => {
                      const id = f._id || f.id;
                      const isSel = inviteSelected.includes(id);
                      const initial = (f.firstName?.[0] || f.email?.[0] || '?').toUpperCase();
                      return (
                        <li
                          key={id}
                          onClick={() => toggleInvite(id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 12px', borderRadius: 8,
                            cursor: 'pointer',
                            background: isSel ? '#eff6ff' : 'transparent',
                            transition: 'background 0.15s',
                          }}
                        >
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            background: isSel ? '#3b82f6' : '#dbeafe',
                            color: isSel ? '#fff' : '#1d4ed8',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: 14, overflow: 'hidden',
                          }}>
                            {f.profileImage
                              ? <img src={f.profileImage} alt={initial} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : initial}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{f.firstName}{f.lastName}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{f.email}</div>
                          </div>
                          <div style={{
                            width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                            border: `2px solid ${isSel ? '#3b82f6' : '#d1d5db'}`,
                            background: isSel ? '#3b82f6' : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {isSel && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
                <button
                  onClick={onClose}
                  style={{ flex: 1, padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#374151' }}
                >
                  건너뛰기
                </button>
                <button
                  onClick={handleInviteFromSheet}
                  disabled={inviteSelected.length === 0}
                  style={{
                    flex: 2, padding: '10px',
                    background: inviteSelected.length > 0 ? '#3b82f6' : '#93c5fd',
                    border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                    cursor: inviteSelected.length > 0 ? 'pointer' : 'not-allowed', color: '#fff',
                  }}
                >
                  초대{inviteSelected.length > 0 ? ` (${inviteSelected.length}명)` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>

        <CustomAlertModal
          isOpen={customAlert.show}
          onClose={closeAlert}
          message={customAlert.message}
          type="warning"
          title="알림"
        />
      </>
    );
  }

  const AI_MODES_CONFIG = [
    { key: 'spellcheck', label: '맞춤법 교정', desc: '메시지를 받거나 보낼 때 맞춤법 자동 교정', activeColor: '#1d4ed8', activeBg: '#dbeafe', activeBorder: '#93c5fd' },
    { key: 'spacing',    label: '띄어쓰기 교정', desc: '메시지를 받거나 보낼 때 띄어쓰기 자동 교정', activeColor: '#15803d', activeBg: '#dcfce7', activeBorder: '#86efac' },
    { key: 'king',       label: '왕모드',      desc: '받은 메시지를 왕실 경어체로 변환 (나만 보임)', activeColor: '#7c3aed', activeBg: '#ede9fe', activeBorder: '#c4b5fd' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: '16px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* 모달 컨테이너 — 팝업 형식, 고정 너비 */}
      <div
        className="bg-white w-full flex flex-col overflow-hidden"
        style={{ borderRadius: 24, maxWidth: 420, maxHeight: '88vh', boxShadow: '0 8px 48px rgba(0,0,0,0.22)' }}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>그룹 만들기</h2>
            <p style={{ fontSize: 12, color: '#9ca3af', margin: 0, marginTop: 2 }}>새로운 조율방을 생성합니다</p>
          </div>
          <button
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={16} color="#6b7280" />
          </button>
        </div>

        {/* 스크롤 영역 */}
        <div className="flex-1 overflow-y-auto px-5" style={{ paddingBottom: 8 }}>
          {/* 방 이름 */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
              방 이름
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  border: '2px solid #e5e7eb', borderRadius: 14, padding: '12px 48px 12px 16px',
                  fontSize: 15, fontWeight: 500, color: '#111827', background: '#f9fafb',
                  outline: 'none', transition: 'border-color 0.15s',
                }}
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 60))}
                placeholder={user?.firstName || '방 이름을 입력하세요'}
                maxLength={60}
                onFocus={e => e.target.style.borderColor = '#3b82f6'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9ca3af' }}>
                {name.length}/60
              </span>
            </div>
          </div>

          {/* AI 모드 토글 버튼 */}
          <div style={{ marginBottom: 12 }}>
            <button
              onClick={() => setShowAiModes(v => !v)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px',
                background: showAiModes ? '#f5f3ff' : '#f9fafb',
                border: `2px solid ${showAiModes ? '#c4b5fd' : '#e5e7eb'}`,
                borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: showAiModes ? '#7c3aed' : '#e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                }}>
                  <Sparkles size={17} color={showAiModes ? '#fff' : '#9ca3af'} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: showAiModes ? '#6d28d9' : '#374151' }}>AI 모드 추가</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                    {showAiModes && roomAiModes.length > 0 ? `${roomAiModes.length}개 선택됨` : '맞춤법, 띄어쓰기, 왕모드'}
                  </div>
                </div>
              </div>
              {/* 토글 스위치 */}
              <div style={{
                width: 46, height: 26, borderRadius: 13, flexShrink: 0,
                background: showAiModes ? '#7c3aed' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  position: 'absolute', top: 3,
                  left: showAiModes ? 23 : 3,
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#fff', transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                }} />
              </div>
            </button>

            {/* AI 모드 카드들 */}
            {showAiModes && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {AI_MODES_CONFIG.map(({ key, label, desc, activeColor, activeBg, activeBorder }) => {
                  const isOn = roomAiModes.includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => setRoomAiModes(prev =>
                        prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]
                      )}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                        padding: '11px 14px',
                        background: isOn ? activeBg : '#f9fafb',
                        border: `2px solid ${isOn ? activeBorder : '#e5e7eb'}`,
                        borderRadius: 12, cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: isOn ? activeColor : '#374151' }}>{label}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{desc}</div>
                      </div>
                      {isOn
                        ? <CheckCircle2 size={20} color={activeColor} style={{ flexShrink: 0 }} />
                        : <Circle size={20} color="#d1d5db" style={{ flexShrink: 0 }} />
                      }
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* 하단 버튼 */}
        <div style={{ padding: '12px 20px 20px', display: 'flex', gap: 10, borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '13px 0', borderRadius: 14,
              border: '2px solid #e5e7eb', background: '#fff',
              fontSize: 14, fontWeight: 600, color: '#6b7280', cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={creating}
            style={{
              flex: 2, padding: '13px 0', borderRadius: 14,
              background: creating ? '#93c5fd' : '#3b82f6', border: 'none',
              fontSize: 14, fontWeight: 700, color: '#fff',
              cursor: creating ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            {creating ? (
              <>
                <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                생성 중...
              </>
            ) : '그룹 만들기'}
          </button>
        </div>
      </div>

      <CustomAlertModal
        isOpen={customAlert.show}
        onClose={closeAlert}
        message={customAlert.message}
        type="warning"
        title="알림"
      />
    </div>
  );
};

export default RoomCreationModal;
